import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Package, ArrowLeft } from "lucide-react";
import { Formik, Form } from "formik";
import { toast, ToastContainer } from "react-toastify";
// Components
import PlanDetailsTab from "./components/PlanDetails";
import PricingLimitsTab from "./components/Pricing";
import FeaturesTab from "./components/Features";
import TrialCreditsTab from "./components/TrialCredits";
import RebillingTab from "./components/Rebilling";

// Types & Validation
import { Plan } from "./components/dataTypes";
import {
  combinedValidationSchema,
  getTabErrors,
} from "./components/utils/validation";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
let initialValues: Plan = {
  title: "",
  description: "",
  saasProducts: [],
  trialPeriod: {
    active: false,
    days: 0,
  },
  credits: {
    active: false,
    creditType: "recurring",
    amount: 0,
  },
  userLimit: 0, // 0 means unlimited
  contactLimit: 0, // 0 means unlimited
  prices: [
    {
      billingInterval: "month",
      active: true,
      amount: 0,
      currency: "USD",
      symbol: "$",
    },
    {
      billingInterval: "year",
      active: true,
      amount: 0,
      currency: "USD",
      symbol: "$",
    },
  ],
  rebilling: [],
};
const ConfigurePlan = () => {
  const AppServies = useAppServices();
  const { user } = useUserInfo();
  const { planId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("plan-details");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);

  const isEditing = Boolean(planId);

  const getPlan = async () => {
    try {
      const { response, error } = await AppServies.saas_plan.get({
        query: `_id=${planId}`,
      });
      if (response) {
        const responseData = response.data;
        initialValues = {
          title: responseData?.title || "",
          description: responseData?.description || "",
          saasProducts: responseData?.saasProducts || [],
          trialPeriod: responseData?.trialPeriod || {
            active: false,
            days: 0,
          },
          credits: responseData?.credits || {
            active: false,
            creditType: "recurring",
            amount: 0,
          },
          userLimit: responseData?.userLimit || 0, // 0 means unlimited
          contactLimit: responseData?.contactLimit || 0, // 0 means unlimited
          prices: responseData?.prices || [
            {
              id: "price_month_" + Date.now(),
              billingInterval: "month",
              active: true,
              amount: 0,
              currency: "USD",
              symbol: "$",
            },
            {
              id: "price_year_" + Date.now(),
              billingInterval: "year",
              active: true,
              amount: 0,
              currency: "USD",
              symbol: "$",
            },
          ],
          rebilling: responseData?.rebilling || [],
        };
        setPlan(response.data);
      }
    } catch (error) {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (planId) {
      getPlan();
    } else {
      setLoading(false);
    }
  }, []);

  const tabs = [
    { id: "plan-details", label: "Plan Details", icon: Package },
    { id: "pricing", label: "Pricing & Limits", icon: Package },
    { id: "features", label: "Features", icon: Package },
    { id: "trial", label: "Trial & Credits", icon: Package },
    { id: "rebilling", label: "Rebilling", icon: Package },
  ];

  const handleSubmit = async (values: Plan, { validateForm }: any) => {
    setIsSubmitting(true);
    try {
      const errors = await validateForm();
      const tabErrorStatus = getTabErrors(errors);
      const errorTabIds = Object.keys(tabErrorStatus).filter(
        (id) => tabErrorStatus[id]
      );

      if (errorTabIds.length > 0) {
        setActiveTab(errorTabIds[0]);
        toast.error("Please fix all validation errors before submitting.");
        return;
      }

      console.log("Submitting:", values);
      const payload = {
        ...values
      };
      if (planId) {
        payload["_id"] = planId;
        var { response } = await AppServies.saas_plan.update({
          payload,
          toaster: true,
        });
      } else {
        payload["companyId"] = user?.companyId;
        var { response } = await AppServies.saas_plan.create({
          payload,
          toaster: true,
        });
      }
      if (response) {
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      toast.error("Unexpected error during submission.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
  };
  if (loading) return <div>Loading...</div>;
  return (
    <div className="w-full bg-white dark:bg-gray-800 py-6 mx-auto px-4 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Plans
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {isEditing ? "Edit Plan" : "Create New Plan"}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {isEditing
            ? "Update your plan configuration and pricing"
            : "Set up your new SaaS plan with features and pricing"}
        </p>
      </div>

      {/* Main Form */}
      <div className="rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <Formik
          initialValues={initialValues}
          validationSchema={combinedValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, setFieldValue, validateForm, submitCount }) => {
            const tabErrorStatus = getTabErrors(errors);

            return (
              <Form>
                {/* Tabs Navigation */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex space-x-8 px-6">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const hasError = tabErrorStatus[tab.id];

                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => handleTabChange(tab.id)}
                          className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === tab.id
                              ? "border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                              : submitCount > 0 && hasError
                              ? "border-red-500 text-red-600 dark:text-red-400 dark:border-red-400"
                              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <Icon size={18} />
                          <span>{tab.label}</span>
                          {submitCount > 0 && hasError && (
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === "plan-details" && (
                    <PlanDetailsTab
                      values={values}
                      onNext={() => handleTabChange("pricing")}
                    />
                  )}

                  {activeTab === "pricing" && (
                    <PricingLimitsTab
                      values={values}
                      setFieldValue={setFieldValue}
                      onPrevious={() => handleTabChange("plan-details")}
                      onNext={() => handleTabChange("features")}
                    />
                  )}

                  {activeTab === "features" && (
                    <FeaturesTab
                      values={values}
                      setFieldValue={setFieldValue}
                      onPrevious={() => handleTabChange("pricing")}
                      onNext={() => handleTabChange("trial")}
                    />
                  )}

                  {activeTab === "trial" && (
                    <TrialCreditsTab
                      values={values}
                      setFieldValue={setFieldValue}
                      onPrevious={() => handleTabChange("features")}
                      onNext={() => handleTabChange("rebilling")}
                    />
                  )}

                  {activeTab === "rebilling" && (
                    <RebillingTab
                      values={values}
                      setFieldValue={setFieldValue}
                      onPrevious={() => handleTabChange("trial")}
                      isSubmitting={isSubmitting}
                      isEditing={isEditing}
                    />
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default ConfigurePlan;
